import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Typography } from '@mui/material';
import { i18n } from 'i18n';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { GridPaginationModel } from '@mui/x-data-grid';
import DataGridWithLoading, { ResponsiveGridColDef } from 'components/DataGrid';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_PAGE_SIZES } from 'config';
import { InspectionPointDto } from 'providers/api';
import React from 'react';

interface InspectionPointActionsMenuProps {
  inspectionPoint: InspectionPointDto;
  assignSpoolLabel?: string;
  onDelete: (inspectionPoint: InspectionPointDto) => void;
  onAssignSpool?: (inspectionPoint: InspectionPointDto) => void;
  onEditSpool?: (inspectionPoint: InspectionPointDto) => void;
}

const InspectionPointActionsMenu = ({
  inspectionPoint,
  assignSpoolLabel,
  onDelete,
  onAssignSpool,
  onEditSpool,
}: InspectionPointActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    onDelete(inspectionPoint);
    setAnchorEl(null);
  };

  const handleAssignSpool = () => {
    onAssignSpool?.(inspectionPoint);
    setAnchorEl(null);
  };

  const handleEditSpool = () => {
    onEditSpool?.(inspectionPoint);
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton color="inherit" aria-controls={`inspection-point-actions-menu-${inspectionPoint.entityId}`} aria-haspopup="true" onClick={handleClick} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu
        id={`inspection-point-actions-menu-${inspectionPoint.entityId}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {onAssignSpool && (
          <MenuItem onClick={handleAssignSpool} disabled={!assignSpoolLabel}>
            <ListItemIcon>
              <LinkIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">
              {assignSpoolLabel
                ? `${i18n._('Assign to spool')} ${assignSpoolLabel}`
                : i18n._('Click a spool to assign this point')}
            </Typography>
          </MenuItem>
        )}
        {onEditSpool && (
          <MenuItem onClick={handleEditSpool}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">{i18n._('Edit spool')}</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteForeverIcon color="error" fontSize="small" />
          </ListItemIcon>
          <Typography color="error" variant="inherit">{i18n._('Delete')}</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

interface InspectionPointsTableProps {
  data: InspectionPointDto[] | undefined;
  page: number;
  pageSize: number;
  totalItems: number | undefined;
  onPaginationModelChange: (paginationModel: GridPaginationModel) => void;
  onDelete: (inspectionPoint: InspectionPointDto) => void;
  onViewInspectionPointWearData: (inspectionPointId: string) => void;
  // How a point's spool is named to a person. A legacy point the migration could not relink has no
  // spool and reads "was at node X" until one is chosen for it.
  describeInspectionPointSpool: (inspectionPoint: InspectionPointDto) => string;
  resolveInspectionPointName: (inspectionPoint: InspectionPointDto) => string;
  assignSpoolLabel?: string;
  onAssignSpool?: (inspectionPoint: InspectionPointDto) => void;
  // Offered only on a point whose spool is in the model.
  canEditSpool?: (inspectionPoint: InspectionPointDto) => boolean;
  onEditSpool?: (inspectionPoint: InspectionPointDto) => void;
  loading: boolean;
  loadingNew: boolean;
  searchTerm: string | null;
}

const InspectionPointsTable = ({
  data,
  onPaginationModelChange,
  onDelete,
  onViewInspectionPointWearData,
  describeInspectionPointSpool,
  resolveInspectionPointName,
  assignSpoolLabel,
  onAssignSpool,
  canEditSpool,
  onEditSpool,
  loadingNew,
  loading,
  page,
  pageSize = PAGINATION_DEFAULT_PAGE_SIZE,
  totalItems = 0,
  searchTerm,
}: InspectionPointsTableProps) => {
  const pageIndex = data ? (page - 1) : 0;
  const rows = data ?? [];

  const columns: ResponsiveGridColDef[] = [
    {
      field: 'spoolId',
      headerName: i18n._('Spool'),
      resizable: true,
      sortable: false,
      filterable: false,
      flex: 1,
      width: 180,
      mobileConfig: {
        width: 180,
        isVisible: true,
      },
      align: 'left',
      renderCell: ({ row }) => {
        const inspectionPoint = row as InspectionPointDto;
        return (
          <Typography variant="body2" color={inspectionPoint.spoolId ? 'textPrimary' : 'warning.main'} noWrap>
            {describeInspectionPointSpool(inspectionPoint)}
          </Typography>
        );
      },
    },
    {
      field: 'name',
      headerName: i18n._('Name'),
      resizable: true,
      sortable: true,
      filterable: true,
      flex: 1,
      width: 150,
      mobileConfig: {
        width: 150,
        isVisible: true,
      },
      align: 'left',
      valueGetter: (_value, row) => resolveInspectionPointName(row as InspectionPointDto),
    },
    {
      field: '',
      sortable: false,
      filterable: false,
      resizable: false,
      width: 40,
      renderCell: ({ row }) => {
        const inspectionPoint = row as InspectionPointDto;
        return (
          <InspectionPointActionsMenu
            inspectionPoint={inspectionPoint}
            onDelete={onDelete}
            assignSpoolLabel={assignSpoolLabel}
            onAssignSpool={!inspectionPoint.spoolId ? onAssignSpool : undefined}
            onEditSpool={canEditSpool?.(inspectionPoint) ? onEditSpool : undefined}
          />
        );
      },
    },
  ];

  return (
    <DataGridWithLoading
      loading={loading}
      density="compact"
      loadingNew={loadingNew}
      searchTerm={searchTerm}
      searchFields={['name']}
      disableRowSelectionOnClick
      disableColumnMenu
      idProp="entityId"
      rows={rows}
      columns={columns}
      pagination
      paginationMode="server"
      paginationModel={{
        page: pageIndex,
        pageSize,
      }}
      pageSize={pageSize}
      rowCount={totalItems}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={PAGINATION_PAGE_SIZES}
      onRowClick={({ row }) => onViewInspectionPointWearData((row as InspectionPointDto).entityId)}
    />
  );
};

export default InspectionPointsTable;
